import { ReactNode, useEffect, useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import ContentEditable from "react-contenteditable";
import { Spacing } from "../settings/SpacingSettings/SpacingSettings";
import TextSettings from "../settings/TextSettings";
import { Font, TextHtmlTag } from "@/components/editor/settings/TextFormatSettings/TextFormatSettings";
import { Background } from "@/components/editor/settings/BackgroundSettings/BackgroundSettings";
import { DisplayProps } from "@/components/editor/settings/DisplaySettings/DisplaySettings";
import { Media } from "@/components/media-manager/MediaManager";
import useVariableValues from "@/components/editor/hooks/useVariableValues";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import useNormalizeSpacing from "@/components/editor/settings/SpacingSettings/useNormalizeSpacing";
import useNormalizeTextFormatSettings from "@/components/editor/settings/TextFormatSettings/useNormalizeTextFormatSettings";
import useNormalizeDisplay from "@/components/editor/settings/DisplaySettings/useNormalizeDisplay";
import useNormalizeBackground from "@/components/editor/settings/BackgroundSettings/useNormalizeBackground";
import { CssClass } from "@/components/editor/settings/ClassSettings";

interface TextProps {
  tag: "h1" | "h2" | "h3" | "p" | "small";
  children: ReactNode;
}

const Text = ({ tag, ...rest }: TextProps) => {
  const {
    connectors: { connect, drag },
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  switch (tag) {
    case "h1":
      return <h1 {...rest} ref={(ref: any) => connect(drag(ref))} />;
    case "h2":
      return <h2 {...rest} ref={(ref: any) => connect(drag(ref))} />;
    case "h3":
      return <h3 {...rest} ref={(ref: any) => connect(drag(ref))} />;
    case "p":
      return <p {...rest} ref={(ref: any) => connect(drag(ref))} />;
    case "small":
      return <small {...rest} ref={(ref: any) => connect(drag(ref))} />;
    default:
      return <p {...rest} ref={(ref: any) => connect(drag(ref))} />;
  }
};

interface TextBlockProps extends Font, Background, DisplayProps, Spacing, CssClass {
  text: string;
  media: Media;
  tag: TextHtmlTag;
}

const TextBlock = (props: Partial<TextBlockProps>) => {
  const { query } = useEditor();
  const responsive = useResponsiveValues();
  const spacing = useNormalizeSpacing(props);
  const textFormat = useNormalizeTextFormatSettings(props);
  const displaySettings = useNormalizeDisplay(props);
  const background = useNormalizeBackground(props);
  const vv = useVariableValues();

  const {
    id,
    actions: { setProp },
    hasSelectedNode,
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  const [editable, setEditable] = useState(false);
  const isEditorEnabled = query.getOptions().enabled;

  useEffect(() => {
    !hasSelectedNode && setEditable(false);
  }, [hasSelectedNode]);

  return (
    <Text key={`${id}-${responsive.viewport}`} tag={responsive.getValue(props.tag)}>
      <ContentEditable
        className={`text-block ${responsive.getValue(props?.cssClass)}`}
        html={vv.getValue(props?.text ?? "")}
        onChange={(e) => {
          setProp((props: TextBlockProps) => (props.text = e.target.value.replace(/<\/?[^>]+(>|$)/g, "")));
        }}
        disabled={!isEditorEnabled || !editable}
        style={{
          ...spacing,
          ...textFormat,
          ...displaySettings,
          ...background,
        }}
        onClick={(_) => setEditable(true)}
      />
    </Text>
  );
};

TextBlock.craft = {
  displayName: "Texto",
  props: {
    text: "Reemplaza",
  },
  related: {
    settings: TextSettings,
  },
};

export default TextBlock;
